import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import Spinner from "react-bootstrap/Spinner";
import { toast, ToastContainer } from "react-toastify";
import config from "../../Config";
import StudentRightsCheck from "./StudentRightsCheck";
import StudentPanelMenu from "./StudentPanelMenu";

function StudentPanelDiary() {
  const [AccessKey] = useState(localStorage.getItem("AccessKey"));
  const [UserID] = useState(parseInt(localStorage.getItem("ID"), 10) || 0);
  const [MemberType] = useState(localStorage.getItem("MemberType") || "Student");
  const [Student_ID] = useState(parseInt(localStorage.getItem("ID"), 10) || 0);

  const [loading, setLoading] = useState(true);
  const [list, setList] = useState([]);
  const [subjectNameById, setSubjectNameById] = useState({});
  const [submissionHistory, setSubmissionHistory] = useState([]);
  const [subjectFilter, setSubjectFilter] = useState("all");
  const [tab, setTab] = useState("today");
  const [uploadingDiaryId, setUploadingDiaryId] = useState(0);
  const [submittingDiaryId, setSubmittingDiaryId] = useState(0);
  const [submissionByDiary, setSubmissionByDiary] = useState({});

  const loadDiaries = useCallback(async () => {
    setLoading(true);
    try {
      const [diaryRes, submissionRes, subjectsRes] = await Promise.all([
        axios({
          method: "post",
          url: config.base_url + "Diary/GetStudentDiaries",
          headers: { "Content-Type": "application/json" },
          data: { AccessKey, UserID, MemberType, Student_ID },
        }),
        axios({
          method: "post",
          url: config.base_url + "Diary/GetMyDiarySubmissions",
          headers: { "Content-Type": "application/json" },
          data: { AccessKey, UserID, MemberType, Student_ID },
        }),
        axios({
          method: "post",
          url: config.base_url + "Exam/GetSubjects",
          headers: { "Content-Type": "application/json" },
          data: {
            AccessKey,
            UserID,
            MemberType,
            PageNo: 1,
            PageSize: 1000,
            SchoolID: 1,
          },
        }),
      ]);

      if (diaryRes.data.status_code === 1) {
        setList(diaryRes.data.ListofRecords || []);
      } else {
        setList([]);
        if (diaryRes.data.status_message) {
          toast.error(diaryRes.data.status_message);
        }
      }

      if (submissionRes.data.status_code === 1) {
        setSubmissionHistory(submissionRes.data.ListofRecords || []);
      } else {
        setSubmissionHistory([]);
      }

      if (subjectsRes.data?.status_code === 1) {
        const map = {};
        (subjectsRes.data.ListofRecords || []).forEach((s) => {
          if (s?.ID > 0) {
            map[s.ID] = s.Name || `Subject ${s.ID}`;
          }
        });
        setSubjectNameById(map);
      } else {
        setSubjectNameById({});
      }
    } catch (e) {
      setList([]);
      setSubmissionHistory([]);
      setSubjectNameById({});
      toast.error("Could not load diary information.");
    } finally {
      setLoading(false);
    }
  }, [AccessKey, UserID, MemberType, Student_ID]);

  useEffect(() => {
    document.title = "Students Diary - Student Panel";
    loadDiaries();
  }, [loadDiaries]);

  const subjects = useMemo(() => {
    const map = new Map();
    list.forEach((row) => {
      const id = row.SubjectID || 0;
      if (id <= 0) return;
      if (!map.has(id)) map.set(id, subjectNameById[id] || `Subject ${id}`);
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [list, subjectNameById]);

  const filteredList = useMemo(() => {
    const today = moment().format("YYYY-MM-DD");
    return list
      .filter((row) => {
        if (subjectFilter === "all") return true;
        return String(row.SubjectID || 0) === String(subjectFilter);
      })
      .filter((row) => {
        if (tab !== "today") return true;
        return moment(row.Dated).format("YYYY-MM-DD") === today;
      });
  }, [list, tab, subjectFilter]);

  const latestSubmissionByDiary = useMemo(() => {
    const map = {};
    submissionHistory.forEach((item) => {
      const diaryId = item.DiaryID || 0;
      if (diaryId <= 0) return;
      if (!map[diaryId]) {
        map[diaryId] = item;
      }
    });
    return map;
  }, [submissionHistory]);

  const updateLocalStatus = (diaryId, patch) => {
    setList((prev) =>
      prev.map((row) => (row.ID === diaryId ? { ...row, ...patch } : row))
    );
  };

  const markRead = (diaryId) => {
    axios({
      method: "post",
      url: config.base_url + "Diary/MarkDiaryRead",
      headers: { "Content-Type": "application/json" },
      data: { AccessKey, UserID, MemberType, Student_ID, DiaryID: diaryId },
    })
      .then((res) => {
        if (res.data.status_code === 1) {
          updateLocalStatus(diaryId, { IsRead: true, ReadOn: moment().format() });
          toast.success("Marked as read.");
        } else {
          toast.error(res.data.status_message || "Could not mark read.");
        }
      })
      .catch(() => toast.error("Could not mark read."));
  };

  const acknowledge = (diaryId) => {
    axios({
      method: "post",
      url: config.base_url + "Diary/AcknowledgeDiary",
      headers: { "Content-Type": "application/json" },
      data: { AccessKey, UserID, MemberType, Student_ID, DiaryID: diaryId },
    })
      .then((res) => {
        if (res.data.status_code === 1) {
          updateLocalStatus(diaryId, {
            ParentAcknowledged: true,
            AcknowledgedOn: moment().format(),
          });
          toast.success("Diary acknowledged.");
        } else {
          toast.error(res.data.status_message || "Could not acknowledge.");
        }
      })
      .catch(() => toast.error("Could not acknowledge."));
  };

  const saveSubmission = (diaryId) => {
    const payload = submissionByDiary[diaryId] || {};
    const SubmissionText = (payload.SubmissionText || "").trim();
    const AttachmentPath = (payload.AttachmentPath || "").trim();
    if (!SubmissionText && !AttachmentPath) {
      toast.info("Please enter submission text or attachment path.");
      return;
    }

    setSubmittingDiaryId(diaryId);
    axios({
      method: "post",
      url: config.base_url + "Diary/SaveDiarySubmission",
      headers: { "Content-Type": "application/json" },
      data: {
        AccessKey,
        UserID,
        MemberType,
        Student_ID,
        DiaryID: diaryId,
        SubmissionText,
        AttachmentPath,
      },
    })
      .then((res) => {
        if (res.data.status_code === 1) {
          toast.success("Homework submitted successfully.");
          setSubmissionByDiary((prev) => ({
            ...prev,
            [diaryId]: { SubmissionText: "", AttachmentPath: "" },
          }));
          loadDiaries();
        } else {
          toast.error(res.data.status_message || "Could not save submission.");
        }
      })
      .catch(() => toast.error("Could not save submission."))
      .finally(() => setSubmittingDiaryId(0));
  };

  const uploadAttachment = (diaryId, file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setUploadingDiaryId(diaryId);
      axios({
        method: "post",
        url: config.base_url + "Diary/UploadDiarySubmissionAttachment",
        headers: { "Content-Type": "application/json" },
        data: {
          AccessKey,
          UserID,
          MemberType,
          Student_ID,
          FileName: file.name,
          FileContent: reader.result,
        },
      })
        .then((res) => {
          if (res.data.status_code === 1) {
            setSubmissionField(
              diaryId,
              "AttachmentPath",
              res.data.AttachmentPath || ""
            );
            toast.success("Attachment uploaded.");
          } else {
            toast.error(res.data.status_message || "Attachment upload failed.");
          }
        })
        .catch(() => toast.error("Attachment upload failed."))
        .finally(() => setUploadingDiaryId(0));
    };
    reader.onerror = () => toast.error("Could not read selected file.");
    reader.readAsDataURL(file);
  };

  const toAbsoluteLink = (path) => {
    if (!path) return "";
    if (/^https?:\/\//i.test(path)) return path;
    const root = config.root || "";
    return `${root.replace(/\/$/, "")}/${String(path).replace(/^\/+/, "")}`;
  };

  const getDiaryHistory = (diaryId) =>
    submissionHistory.filter((s) => s.DiaryID === diaryId);

  const setSubmissionField = (diaryId, key, value) => {
    setSubmissionByDiary((prev) => ({
      ...prev,
      [diaryId]: {
        SubmissionText: prev[diaryId]?.SubmissionText || "",
        AttachmentPath: prev[diaryId]?.AttachmentPath || "",
        [key]: value,
      },
    }));
  };

  return (
    <>
      <StudentPanelMenu />
      <ToastContainer />
      <StudentRightsCheck />
      <div className="container body-content">
        <div className="page-head">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-md-8">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <Link to="/student-panel/dashboard">Dashboard</Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Students Diary
                    </li>
                  </ol>
                </nav>
                <div className="page-title">Students Diary</div>
              </div>
            </div>
          </div>
        </div>

        <div className="card mt-3">
          <div className="card-body d-flex flex-wrap gap-2 align-items-center">
            <div className="btn-group" role="group">
              <button
                type="button"
                className={`btn btn-sm ${tab === "today" ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => setTab("today")}
              >
                Today's Diary
              </button>
              <button
                type="button"
                className={`btn btn-sm ${tab === "history" ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => setTab("history")}
              >
                Diary History
              </button>
            </div>
            <select
              className="form-select form-select-sm"
              style={{ width: 220 }}
              value={subjectFilter}
              onChange={(e) => setSubjectFilter(e.target.value)}
            >
              <option value="all">All subjects</option>
              {subjects.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary ms-auto"
              onClick={loadDiaries}
            >
              Refresh
            </button>
          </div>
        </div>

        <div className="mt-3 pb-5">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" role="status" />
            </div>
          ) : filteredList.length === 0 ? (
            <div className="card">
              <div className="card-body text-center py-4 text-muted">
                No diary entries found for this selection.
              </div>
            </div>
          ) : (
            filteredList.map((row) => (
              <div className="card mb-3" key={row.ID}>
                <div className="card-body">
                  <div className="d-flex justify-content-between gap-2 flex-wrap">
                    <div>
                      <h5 className="mb-1">{row.DiaryTitle || "Diary note"}</h5>
                      <div className="small text-muted">
                        Date:{" "}
                        {row.Dated ? moment(row.Dated).format("DD MMM YYYY") : "-"}
                        {row.DueDate
                          ? ` | Due: ${moment(row.DueDate).format("DD MMM YYYY")}`
                          : ""}
                        {row.SubjectID
                          ? ` | Subject: ${subjectNameById[row.SubjectID] || `Subject ${row.SubjectID}`}`
                          : ""}
                      </div>
                    </div>
                    <div className="text-end small">
                      <div>
                        Read:{" "}
                        <strong>{row.IsRead ? "Yes" : "No"}</strong>
                      </div>
                      <div>
                        Acknowledged:{" "}
                        <strong>{row.ParentAcknowledged ? "Yes" : "No"}</strong>
                      </div>
                      {latestSubmissionByDiary[row.ID] ? (
                        <div>
                          Submission:{" "}
                          <strong>
                            {latestSubmissionByDiary[row.ID].Status || "Submitted"}
                          </strong>
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <p className="mt-3 mb-3" style={{ whiteSpace: "pre-wrap" }}>
                    {row.DiaryText}
                  </p>

                  {Array.isArray(row.Attachments) && row.Attachments.length > 0 ? (
                    <div className="mb-3">
                      <div className="fw-semibold mb-1">Attachments</div>
                      <div className="d-flex flex-column gap-1">
                        {row.Attachments.map((a) => (
                          <a
                            key={a.ID}
                            href={toAbsoluteLink(a.FilePath)}
                            target="_blank"
                            rel="noreferrer"
                            download
                          >
                            {a.FileName || "Download file"}
                          </a>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  <div className="d-flex flex-wrap gap-2 mb-3">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      disabled={!!row.IsRead}
                      onClick={() => markRead(row.ID)}
                    >
                      {row.IsRead ? "Read" : "Mark as Read"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-success"
                      disabled={!!row.ParentAcknowledged}
                      onClick={() => acknowledge(row.ID)}
                    >
                      {row.ParentAcknowledged ? "Acknowledged" : "Acknowledge"}
                    </button>
                  </div>

                  {row.IsSubmissionRequired ? (
                    <div className="border rounded p-3 bg-light">
                      <div className="fw-semibold mb-2">Submit Homework</div>
                      <textarea
                        className="form-control mb-2"
                        rows={3}
                        placeholder="Write your submission..."
                        value={submissionByDiary[row.ID]?.SubmissionText || ""}
                        onChange={(e) =>
                          setSubmissionField(row.ID, "SubmissionText", e.target.value)
                        }
                      />
                      <input
                        type="file"
                        className="form-control mb-2"
                        onChange={(e) => uploadAttachment(row.ID, e.target.files?.[0])}
                      />
                      <input
                        type="text"
                        className="form-control mb-2"
                        placeholder="Attachment path"
                        value={submissionByDiary[row.ID]?.AttachmentPath || ""}
                        onChange={(e) => setSubmissionField(row.ID, "AttachmentPath", e.target.value)}
                      />
                      {uploadingDiaryId === row.ID ? (
                        <div className="small text-muted mb-2">Uploading attachment...</div>
                      ) : null}
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        disabled={submittingDiaryId === row.ID || uploadingDiaryId === row.ID}
                        onClick={() => saveSubmission(row.ID)}
                      >
                        {submittingDiaryId === row.ID ? "Submitting..." : "Submit"}
                      </button>

                      {latestSubmissionByDiary[row.ID] ? (
                        <div className="mt-3 small">
                          <div>
                            <strong>Latest status:</strong>{" "}
                            {latestSubmissionByDiary[row.ID].Status || "Submitted"}
                          </div>
                          <div>
                            <strong>Submitted on:</strong>{" "}
                            {latestSubmissionByDiary[row.ID].SubmittedOn
                              ? moment(latestSubmissionByDiary[row.ID].SubmittedOn).format(
                                  "DD MMM YYYY, hh:mm A"
                                )
                              : "-"}
                          </div>
                          <div>
                            <strong>Teacher remarks:</strong>{" "}
                            {latestSubmissionByDiary[row.ID].TeacherRemarks || "-"}
                          </div>
                          {latestSubmissionByDiary[row.ID].AttachmentPath ? (
                            <div>
                              <a
                                href={toAbsoluteLink(latestSubmissionByDiary[row.ID].AttachmentPath)}
                                target="_blank"
                                rel="noreferrer"
                              >
                                View latest attachment
                              </a>
                            </div>
                          ) : null}
                        </div>
                      ) : null}

                      {getDiaryHistory(row.ID).length > 0 ? (
                        <div className="table-responsive mt-3">
                          <table className="table table-sm mb-0">
                            <thead>
                              <tr>
                                <th>Submitted On</th>
                                <th>Status</th>
                                <th>Teacher Remarks</th>
                                <th>Attachment</th>
                              </tr>
                            </thead>
                            <tbody>
                              {getDiaryHistory(row.ID).map((item) => (
                                <tr key={item.ID}>
                                  <td>
                                    {item.SubmittedOn
                                      ? moment(item.SubmittedOn).format("DD MMM YYYY, hh:mm A")
                                      : "-"}
                                  </td>
                                  <td>{item.Status || "-"}</td>
                                  <td>{item.TeacherRemarks || "-"}</td>
                                  <td>
                                    {item.AttachmentPath ? (
                                      <a
                                        href={toAbsoluteLink(item.AttachmentPath)}
                                        target="_blank"
                                        rel="noreferrer"
                                      >
                                        Open
                                      </a>
                                    ) : (
                                      "-"
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
}

export default StudentPanelDiary;
